import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, CreditCard, TrendingDown, Zap, Target } from "lucide-react";
import Modal from "../components/Modal";
import ProgressBar from "../components/ProgressBar";
import SummaryCard from "../components/SummaryCard";
import { getDebts, createDebt, updateDebt, deleteDebt } from "../utils/api";
import { calcDebtPayoff, fmt } from "../utils/calculations";
import { format, addMonths } from "date-fns";

const EMPTY = { name: "", balance: "", minimumPayment: "", apr: "", dueDate: "" };

export default function DebtTracker() {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [strategy, setStrategy] = useState("avalanche");
  const [extraPayment, setExtraPayment] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  async function load() {
    setLoading(true);
    try { setDebts(await getDebts()); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setModalOpen(true);
  }

  function openEdit(d) {
    setEditing(d);
    setForm({
      name: d.name || "",
      balance: d.balance?.toString() || "",
      minimumPayment: d.minimumPayment?.toString() || "",
      apr: d.apr?.toString() || "",
      dueDate: d.dueDate || "",
    });
    setError("");
    setModalOpen(true);
  }

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    if (!form.name || !form.balance || !form.minimumPayment) {
      setError("Name, balance, and minimum payment are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const updated = await updateDebt(editing.id, form);
        setDebts((prev) => prev.map((d) => (d.id === editing.id ? { ...d, ...updated } : d)));
      } else {
        const created = await createDebt(form);
        setDebts((prev) => [created, ...prev]);
      }
      setModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteDebt(id);
      setDebts((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteConfirm(null);
    }
  }

  const extra = parseFloat(extraPayment) || 0;
  const payoffAvalanche = calcDebtPayoff(debts, "avalanche", extra);
  const payoffSnowball  = calcDebtPayoff(debts, "snowball",  extra);
  const payoffCurrent   = strategy === "avalanche" ? payoffAvalanche : payoffSnowball;

  const totalDebt = debts.reduce((s, d) => s + (d.balance || 0), 0);
  const totalOriginal = debts.reduce((s, d) => s + (d.originalBalance || d.balance || 0), 0);
  const totalMinPayment = debts.reduce((s, d) => s + (d.minimumPayment || 0), 0);

  // Sort debts visually based on strategy
  const sortedDebts = [...debts].sort((a, b) =>
    strategy === "avalanche" ? (b.apr || 0) - (a.apr || 0) : (a.balance || 0) - (b.balance || 0)
  );

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Debt Tracker</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Plan your payoff strategy</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <Plus className="w-4 h-4" /> Add Debt
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <SummaryCard title="Total Remaining" value={fmt(totalDebt)} subtitle="current balance" icon={CreditCard} color="red" />
        <SummaryCard title="Total Paid Off" value={fmt(totalOriginal - totalDebt)} subtitle="from original" icon={TrendingDown} color="green" />
        <SummaryCard title="Min. Payments" value={fmt(totalMinPayment)} subtitle="per month" icon={CreditCard} color="yellow" />
        <SummaryCard title="Accounts" value={debts.length} subtitle="tracked" icon={CreditCard} color="blue" />
      </div>

      {/* Strategy selector + extra payment */}
      {debts.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Payoff Strategy</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Avalanche */}
            <button
              onClick={() => setStrategy("avalanche")}
              className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all ${
                strategy === "avalanche"
                  ? "border-primary-500 bg-primary-50 dark:bg-primary-900/30"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap className={`w-5 h-5 ${strategy === "avalanche" ? "text-primary-600 dark:text-primary-400" : "text-gray-400 dark:text-gray-500"}`} />
                <span className="font-semibold text-gray-900 dark:text-gray-100">Avalanche</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Pay highest interest rate first. Minimizes total interest paid.</p>
              <div className="text-xs font-medium text-primary-600 dark:text-primary-400">
                {payoffAvalanche.months}mo — {fmt(payoffAvalanche.totalInterest)} interest
              </div>
            </button>

            {/* Snowball */}
            <button
              onClick={() => setStrategy("snowball")}
              className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all ${
                strategy === "snowball"
                  ? "border-secondary-500 bg-secondary-50 dark:bg-secondary-900/30"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
            >
              <div className="flex items-center gap-2">
                <Target className={`w-5 h-5 ${strategy === "snowball" ? "text-secondary-600 dark:text-secondary-400" : "text-gray-400 dark:text-gray-500"}`} />
                <span className="font-semibold text-gray-900 dark:text-gray-100">Snowball</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Pay smallest balance first. Builds momentum with quick wins.</p>
              <div className="text-xs font-medium text-secondary-600 dark:text-secondary-400">
                {payoffSnowball.months}mo — {fmt(payoffSnowball.totalInterest)} interest
              </div>
            </button>

            {/* Extra payment */}
            <div className="flex flex-col gap-2 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">Extra Payment</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Add extra monthly payment on top of minimums.</p>
              <input
                type="number"
                min="0"
                step="10"
                placeholder="$0 extra/mo"
                className="input mt-auto"
                value={extraPayment}
                onChange={(e) => setExtraPayment(e.target.value)}
              />
            </div>
          </div>

          {/* Strategy comparison */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Projected payoff date ({strategy})</p>
              <p className="font-bold text-gray-900 dark:text-gray-100">
                {payoffCurrent.projectedDate
                  ? format(payoffCurrent.projectedDate, "MMM yyyy")
                  : "—"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{payoffCurrent.months} months</p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total interest ({strategy})</p>
              <p className="font-bold text-red-600 dark:text-red-400">{fmt(payoffCurrent.totalInterest)}</p>
              {strategy === "snowball" && payoffAvalanche.totalInterest < payoffSnowball.totalInterest && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  Switch to avalanche → save {fmt(payoffSnowball.totalInterest - payoffAvalanche.totalInterest)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Debt list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : debts.length === 0 ? (
        <div className="card text-center py-16">
          <CreditCard className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">No debts tracked</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Add credit cards, loans, or any other debt to start your payoff plan.</p>
          <button onClick={openAdd} className="btn-primary inline-flex">
            <Plus className="w-4 h-4" /> Add Debt
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedDebts.map((d, idx) => {
            const original = d.originalBalance || d.balance;
            const paidOff = original - d.balance;
            const pct = original > 0 ? (paidOff / original) * 100 : 0;
            const detail = payoffCurrent.debtDetails?.find((dd) => dd.id === d.id);

            return (
              <div key={d.id} className="card">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{d.name}</h3>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>APR: <strong className="text-red-600 dark:text-red-400">{d.apr?.toFixed(2) || 0}%</strong></span>
                        <span>Min: <strong>{fmt(d.minimumPayment)}/mo</strong></span>
                        {d.dueDate && <span>Due: <strong>{new Date(d.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</strong></span>}
                        {detail && <span className="text-primary-600 dark:text-primary-400">~{detail.months}mo to payoff</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{fmt(d.balance)}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">of {fmt(original)}</p>
                    </div>
                    <button onClick={() => openEdit(d)} className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteConfirm(d.id)} className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <ProgressBar
                  value={paidOff}
                  max={original}
                  color={pct >= 75 ? "green" : pct >= 40 ? "blue" : "red"}
                  showLabel={false}
                  size="md"
                />
                <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>{pct.toFixed(0)}% paid off</span>
                  <span>{fmt(d.balance)} remaining</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Debt" : "Add Debt"}>
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>}
          <div>
            <label className="label">Debt name *</label>
            <input className="input" name="name" placeholder="e.g. Chase Credit Card" value={form.name} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Current balance *</label>
              <input className="input" type="number" name="balance" min="0" step="0.01" placeholder="5000.00"
                value={form.balance} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Minimum payment *</label>
              <input className="input" type="number" name="minimumPayment" min="0" step="0.01" placeholder="150.00"
                value={form.minimumPayment} onChange={handleChange} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Interest rate (APR %)</label>
              <input className="input" type="number" name="apr" min="0" max="100" step="0.01" placeholder="19.99"
                value={form.apr} onChange={handleChange} />
            </div>
            <div>
              <label className="label">Payment due date</label>
              <input className="input" type="date" name="dueDate" value={form.dueDate} onChange={handleChange} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Debt"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Debt" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">Are you sure you want to delete this debt?</p>
        <div className="flex gap-3">
          <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button onClick={() => handleDelete(deleteConfirm)} className="btn-danger flex-1 justify-center">Delete</button>
        </div>
      </Modal>
    </div>
  );
}
